import React from 'react';
import { HourglassIcon } from 'lucide-react';
import { useAppState } from '../../contexts/AppStateContext';
import { PlanLine } from '../../utils/progress';
import { HelpTip } from '../ui/HelpTip';
interface WeekPlanCardProps {
  memberId: string;
}
function lineDetail(l: PlanLine): string {
  const parts: string[] = [];
  if (l.excusedDays > 0) parts.push('oproščeno – se ne šteje');
  if (!l.weighted) {
    if (l.kind === 'sport') parts.push(`${l.units} od ${l.required} vadb`);else parts.push(`${l.units} od ${l.required} dni`);
  }
  if (l.fromCalendar) parts.push(`${l.fromCalendar}× iz koledarja`);
  if (l.hint) parts.push(l.hint);
  return parts.join(' · ');
}
export function WeekPlanCard({
  memberId
}: WeekPlanCardProps) {
  const {
    family,
    viewer,
    memberById,
    weekFor,
    schoolPEFor,
    setSchoolPE,
    isAdmin
  } = useAppState();
  const member = memberById(memberId);
  const week = weekFor(memberId);
  const schoolPE = schoolPEFor(memberId);
  const canMarkPE = member?.role === 'otrok' && memberId === viewer.id;
  const own = memberId === viewer.id;
  const needed = Math.max(0, Math.ceil(week.threshold * week.possible / 100) - week.earned);
  const kid = member?.role === 'otrok';
  const r = family.rules;
  return <section aria-labelledby={`week-${memberId}`} className="rounded-3xl border-[1.5px] border-line bg-surface p-5 shadow-card md:p-7" data-id="element-410" data-mp="3ejof9jzbtkad">
      <div className="flex items-center justify-between gap-3" data-id="element-411" data-mp="b2el36ni3mm7">
        <h2 id={`week-${memberId}`} className="text-sm font-extrabold text-muted" data-id="element-412" data-mp="b1wq0dckrefv">
          {own ? 'Tvoj teden' : `Teden · ${member?.name}`} · 5.–11. oktober
        </h2>
        <HelpTip label="Kako se računajo cvenkotočke?" data-id="element-413" data-mp="13784kkohtzm">
          Vsaka redna obveznost prinese cvenkotočke. Odstotek pove, koliko cvenkotočk tega tedna je že zbranih.
          {kid ? ` Pri ${week.threshold} % je kačing – žepnina ${r.allowanceAmount} ${r.currency}.` : ''} Oproščene naloge se ne štejejo. Goodies so posebej in
          odstotka ne zvišujejo.
        </HelpTip>
      </div>

      <div className="mt-2 flex flex-wrap items-end gap-x-4 gap-y-1" data-id="element-414" data-mp="18x8tdrnh94l4">
        <p className="font-display text-5xl leading-none tabular-nums md:text-6xl" data-id="element-415" data-mp="1cxixeyrplkxc">
          {week.earned}
          <span className="text-2xl text-muted md:text-3xl" data-id="element-416" data-mp="yyjbvzzhtm5e"> / {week.possible}</span>
        </p>
        <p className="pb-1 text-sm font-bold text-muted" data-id="element-417" data-mp="13wl4pcmq0zu2">cvenkotočk</p>
        <p className={`ml-auto pb-0.5 font-display text-4xl tabular-nums ${week.reached ? 'text-done-ink' : 'text-accent-ink'}`} data-id="element-418" data-mp="13yxjxi13gw3f">{week.pct} %</p>
      </div>

      <div className="relative mt-4 h-4 rounded-full bg-line" aria-hidden="true" data-id="element-419" data-mp="tzq84za0d9if">
        <div className={`h-full rounded-full ${week.reached ? 'bg-done' : 'bg-accent'}`} style={{
        width: `${week.pct}%`,
        transition: 'width 260ms cubic-bezier(0.23, 1, 0.32, 1)'
      }} data-id="element-420" data-mp="g8bd1u2tthv0" />
        <div className="absolute -top-1.5 h-7 w-1 rounded-full bg-ink" style={{
        left: `${week.threshold}%`
      }} data-id="element-421" data-mp="6adpz9wpdr3z" />
      </div>
      <p className={`mt-2 text-sm font-extrabold ${week.reached ? 'text-done-ink' : 'text-ink'}`} data-id="element-422" data-mp="b7nn7bqc372d">
        {week.excusedWeek ? 'Oproščen teden – žepnina ostane.' : week.reached ? kid ? 'KAČING! Žepnina je odklenjena.' : 'Tedenski cilj dosežen.' : `Še ${needed} cvenkotočk do ${kid ? 'kačinga' : 'cilja'} (${week.threshold} %).`}
      </p>

      <ul className="mt-5 divide-y divide-line" data-id="element-423" data-mp="3st7cacgizbm2">
        {week.lines.map(l => <li key={l.id} className="py-3" data-id="element-424" data-mp="ajomctyjv1mg">
            <div className="flex items-center gap-3" data-id="element-425" data-mp="6k5zvtr2yihi">
              <span className="min-w-0 flex-1" data-id="element-426" data-mp="6jnw8c2391zk">
                <span className="block font-bold" data-id="element-427" data-mp="kbucdy58r8z0">{l.label}</span>
                <span className={`block text-xs ${l.excusedDays > 0 ? 'text-excused-ink' : 'text-muted'}`} data-id="element-428" data-mp="kdfatyuofc25">{lineDetail(l)}</span>
              </span>
              {l.pendingReview && <span className="inline-flex items-center gap-1 rounded-full bg-review-soft px-2 py-0.5 text-xs font-bold text-review-ink" data-id="element-429" data-mp="u9ql6japhrty">
                  <HourglassIcon className="h-3 w-3" aria-hidden="true" data-id="element-430" data-mp="2iteegy6bu78d" /> {isAdmin ? 'čaka potrditev' : 'šteje po potrditvi'}
                </span>}
              <span className="w-16 text-right font-extrabold tabular-nums" data-id="element-431" data-mp="2nq4r1bixakp1">
                {l.earned}/{l.possible}
              </span>
            </div>
            {l.kind === 'sport' && kid && <label className={`mt-2 flex items-center gap-3 rounded-2xl bg-canvas px-3 py-2.5 text-sm ${canMarkPE ? 'cursor-pointer' : ''}`} data-id="element-432" data-mp="2xk3npv089c9l">
                <input type="checkbox" checked={schoolPE} disabled={!canMarkPE} onChange={e => setSchoolPE(e.target.checked)} className="h-5 w-5 rounded accent-[#C24E28]" data-id="element-433" data-mp="24sk07ywllc1s" />
                <span className="flex-1" data-id="element-434" data-mp="3l7vchy2yfhrr">
                  <span className="block font-bold" data-id="element-435" data-mp="3l6tpqwckt6u7">Ta teden imam športno v šoli</span>
                  <span className="block text-xs text-muted" data-id="element-436" data-mp="3q5n5kvfc59xj">
                    Potem zadostujeta {r.sportPerWeekChildWithSchoolPE} vadbi namesto {r.sportPerWeekChild}.
                  </span>
                </span>
              </label>}
          </li>)}
      </ul>
    </section>;
}